// src/main.tsx
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import "./i18n";
import { pwaInfo } from "virtual:pwa-info";
import { applyFontSizePreference, getFontSizePreference } from "@/lib/fontSize";

// Apply user font-size preference ASAP to reduce layout flash.
applyFontSizePreference(getFontSizePreference());

console.log("🔥 InnerGem main.tsx booting in", import.meta.env.MODE);

// 注册 Service Worker 并输出状态（dev 时需要带正确 type=module 才能加载 dev-sw.js 的 import）
try {
  if ("serviceWorker" in navigator) {
    const swUrl = pwaInfo?.registerSW?.inlinePath || "/sw.js";
    const swScope = pwaInfo?.registerSW?.scope || "/";

    // With injectManifest + format "es", dev SW uses ESM (dev-sw.js has imports).
    // Register as module in dev to avoid "import outside a module" / evaluation failures.
    const swType: "classic" | "module" = import.meta.env.DEV
      ? "module"
      : (pwaInfo?.registerSW?.type || "classic");

    (async () => {
      try {
        if (import.meta.env.DEV) {
          const existing = await navigator.serviceWorker.getRegistrations();
          await Promise.all(existing.map((r) => r.unregister()));
        }

        const registration = await navigator.serviceWorker.register(swUrl, {
          type: swType,
          scope: swScope,
        });

        console.log("✅ SW registered:", registration.scope, "type:", swType, "url:", swUrl);
        const sw = registration.active || registration.installing || registration.waiting;
        if (sw) {
          console.log("SW state:", sw.state);
          sw.addEventListener("statechange", () => {
            console.log("SW state changed:", sw.state);
          });
        }
      } catch (error) {
        console.error("❌ SW registration error:", error);
      }
    })();
  }
} catch (e) {
  console.warn("SW register attempt failed:", e);
}

// 一个简单的 Error Boundary，出错时把 message 打到 console
class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("RootErrorBoundary caught error:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24 }}>
          <h2>Something went wrong.</h2>
          <p>{this.state.error.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error('Root element "#root" not found in index.html');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);